import { ReactTFC } from "../../../../../utils/fp/missing-ts-parts";
import React, { useState } from "react";
import { NewsletterSignUpPayloadT } from "@swo/shared";
import { DealerLocatorSegment, NewsletterLandingPageDataData, type TranslatedValue } from "@swo/storefront";

import { css } from "@emotion/react";

import { CSS_COLORS, CSS_SPACER_SIZES } from "../../../../../styles/styles-css-variables";
import { Spacer } from "../../../../../components/_shared-components/arrangement/Space";
import { SwarovskiRadio } from "../../../../../components/_shared-components/ui-elements/SwarovskiRadio";
import { SwarovskiButton } from "../../../../../components/_shared-components/ui-elements/SwarovskiButton";
import { CSS_MQ } from "../../../../../styles/breakpoints";
import { pipe } from "fp-ts/lib/function";
import { nonEmptyArray, option } from "fp-ts";
import { CountryAndLanguageSelector } from "./CountryAndLanguageSelector";
import { SegmentSelector } from "./SegmentSelector";
import { MailInput } from "./MailInput";
import { fontColor, FONTS, fontSize } from "../../../../../styles/fonts";
import { storefrontClient } from "../../../../../domain/storefront";
import { DealerDto } from "@swo/salesforce-service";
import { InterpolateLinearlyReact } from "../../../../../domain/translations/interpolate-linearly-react";
import { T } from "../../../../../components/T";

const supportedSegments: Array<NonNullable<DealerDto["segment"]>> = ["hunting", "birding", "outdoor"];

export const SignUpForm: ReactTFC<{
  countries: NewsletterLandingPageDataData["countries"];
  languages: NewsletterLandingPageDataData["languages"];
  currentLanguageIsoCode: string;
  currentCountryIsoCode: string;
  currentSegment: string;
  segmentLabels: { birding: TranslatedValue; hunting: TranslatedValue; outdoor: TranslatedValue };

  maybeInitialEmailValue: string | undefined;
  signUpTermsParagraph: React.ReactNode;
  buttonLabel: React.ReactNode;
  segmentSelectorHeadline: React.ReactNode;
  termsAndConditions: { link: string; label: TranslatedValue };
  otherConditions: { link: string; label: TranslatedValue };
  translations: { iAccept: TranslatedValue; and: TranslatedValue; placeholderLabel: string };
  onSignedUp: () => void;
}> = props => {
  const [email, setEmail] = useState(props.maybeInitialEmailValue || "");
  const [segments, setSegments] =
    useState<Array<{ name: NonNullable<DealerDto["segment"]>; label: string; checked: boolean }>>(
      getInitialSegmentsState(),
    );

  function getInitialSegmentsState(): Array<{
    name: NonNullable<DealerDto["segment"]>;
    label: string;
    checked: boolean;
  }> {
    return supportedSegments.map(segment => {
      return {
        name: segment,
        label: props.segmentLabels[segment]?.value || segment,
        checked: props.currentSegment === segment,
      };
    });
  }

  const [selectedCountry, setSelectedCountry] =
    useState<NewsletterLandingPageDataData["countries"][0]>(getInitiallySelectedCountry());

  const [selectedLanguage, setSelectedLanguage] = useState<NewsletterLandingPageDataData["languages"]["fallback"][0]>(
    getInitiallySelectedLanguage(selectedCountry.isoCode),
  );

  const languages = getLanguagesForCountry(selectedCountry.isoCode);

  function getLanguagesForCountry(countryIsoCode: string): NewsletterLandingPageDataData["languages"]["fallback"] {
    return props.languages[countryIsoCode.toLowerCase()] || props.languages.fallback;
  }

  function getInitiallySelectedCountry(): NewsletterLandingPageDataData["countries"][0] {
    return pipe(
      props.countries.find(country => country.isoCode.toLowerCase() === props.currentCountryIsoCode.toLowerCase()),
      option.fromNullable,
      option.getOrElse(() => {
        return pipe(props.countries, nonEmptyArray.head);
      }),
    );
  }

  function getInitiallySelectedLanguage(
    countryIsoCode: string,
  ): NewsletterLandingPageDataData["languages"]["fallback"][0] {
    const languages = getLanguagesForCountry(countryIsoCode);
    return pipe(
      languages.find(language => language.isoCode.toLowerCase() === props.currentLanguageIsoCode.toLowerCase()),
      option.fromNullable,
      option.getOrElse(() => {
        return pipe(languages, nonEmptyArray.head);
      }),
    );
  }

  const hasAtLeastOneSegmentSelected = Object.values(segments).some(v => !!v.checked);

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();

    const segmentsArr = segments
      .filter(segment => {
        return segment.checked;
      })
      .map(s => s.name);

    const body: NewsletterSignUpPayloadT = {
      email,
      segments: segmentsArr,
      selectedCountryIsoCode: selectedCountry.isoCode,
      selectedLanguageIsoCode: selectedLanguage.isoCode,
    };

    void storefrontClient.newsletterSignUp(body).finally(() => {
      props.onSignedUp();
    });
  }

  const segmentHeadline = [
    FONTS.SwarovskiFutura500,
    fontColor.black,
    fontSize.x2,
    css`
      text-transform: uppercase;
      margin-bottom: 40px;

      ${CSS_MQ.atMostSm} {
        margin-bottom: 20px;
      }

      display: block;
      margin-right: auto;
    `,
  ];

  return (
    <form
      onSubmit={onSubmit}
      css={css`
        display: flex;
        flex-direction: column;
        align-items: center;
      `}
    >
      <MailInput email={email} setEmail={setEmail} placeholderLabel={props.translations.placeholderLabel} />

      <Spacer size={"lg"} />

      <span css={segmentHeadline}>{props.segmentSelectorHeadline}</span>

      <SegmentSelector segments={segments} setSegments={setSegments} />

      <CountryAndLanguageSelector
        countries={props.countries}
        languages={languages}
        selectedCountry={selectedCountry}
        selectedLanguage={selectedLanguage}
        onCountryChange={country => {
          setSelectedCountry(country);
          setSelectedLanguage(getInitiallySelectedLanguage(country.isoCode));
        }}
        onLanguageChange={language => setSelectedLanguage(language)}
      />

      <Spacer size={"md"} />

      <SwarovskiRadio
        type={"checkbox"}
        inputProps={{
          required: true,
        }}
        labelProps={{}}
        attachStyles={css`
          display: flex;
          align-items: flex-end;

          input {
            flex-shrink: 0;
          }

          p:last-child {
            margin-bottom: 0;
          }

          p:first-child {
            margin-top: 0;
          }

          p {
            ${CSS_MQ.atLeastMd} {
              margin-left: 12px;
            }
            color: black;

            font-weight: 300;
          }
        `}
      >
        <div>
          <p>{props.signUpTermsParagraph}</p>

          <p>
            <InterpolateLinearlyReact
              translationStringWithInterpolatedValues={`${props.translations.iAccept.value} <terms> ${props.translations.and.value} <otherConditions>.`}
              args={[
                <a href={props.termsAndConditions.link} target={"_blank"} css={bolder}>
                  <T>{props.termsAndConditions.label}</T>
                </a>,
                <a href={props.otherConditions.link} target={"_blank"} css={bolder}>
                  <T>{props.otherConditions.label}</T>
                </a>,
              ]}
            />
          </p>
        </div>
      </SwarovskiRadio>

      <Spacer size={"md"} />

      <span
        css={css`
          margin-bottom: ${CSS_SPACER_SIZES.md};
          display: flex;
          justify-content: center;
        `}
      >
        <SwarovskiButton variant={"green"} type={"submit"} as={"button"} disabled={!hasAtLeastOneSegmentSelected}>
          {props.buttonLabel}
        </SwarovskiButton>
      </span>
    </form>
  );
};

const bolder = css`
  .disabled & {
    color: inherit;
  }

  color: ${CSS_COLORS.Green};

  font-weight: 500;
`;
